import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Divider,
  FormField,
  Input,
  PrimaryButton,
  SecondaryButton,
  Select,
} from "../../design-system";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import {
  useApplyGlobalAccessGrantBatch,
  useGlobalAccessGrant,
  useRevokeGlobalAccessGrantBatch,
  useUpsertGlobalAccessGrant,
} from "../hooks/useAdminGlobalAccessGrant";

const DEFAULT_LIMIT = 200;

// Status color patterns (explicit dark variants allowed for semantic status states)
const statusStyles = {
  success: "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
  warning: "bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300",
  error: "bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
  neutral: `${surfaces.tertiary} border ${borders.default} ${textColors.primary}`,
};

const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toIsoFromDatetimeLocal = (value) => {
  const v = String(value || "").trim();
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const formatLocalDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
};

const computeGrantStatus = (g) => {
  if (!g) {
    return {
      badge: "Not loaded",
      tone: "neutral",
      subtitle: "Loading current grant…",
    };
  }

  const plan = String(g.plan_name || "plus").toUpperCase();
  const active = Boolean(g.active);
  const now = Date.now();
  const endsAt = g.expires_at ? new Date(g.expires_at).getTime() : null;
  const hasValidEndsAt = typeof endsAt === "number" && !Number.isNaN(endsAt);
  const isExpired = active && hasValidEndsAt && endsAt <= now;
  const hoursLeft = active && hasValidEndsAt ? Math.max(0, Math.round((endsAt - now) / 3600000)) : null;

  if (!active) {
    return {
      badge: "Disabled",
      tone: "neutral",
      subtitle: `Not currently applying (${plan}).`,
    };
  }

  if (isExpired) {
    return {
      badge: "Expired",
      tone: "warning",
      subtitle: `Configured as active (${plan}) but the end time has passed.`,
    };
  }

  return {
    badge: "Enabled",
    tone: "success",
    subtitle: hasValidEndsAt ? `Currently applying (${plan}). Ends in ~${hoursLeft}h.` : `Currently applying (${plan}).`,
  };
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const GlobalAccessGrantAdminPage = () => {
  const grantQuery = useGlobalAccessGrant();
  const upsertMutation = useUpsertGlobalAccessGrant();
  const applyMutation = useApplyGlobalAccessGrantBatch();
  const revokeMutation = useRevokeGlobalAccessGrantBatch();

  const [form, setForm] = useState({
    active: false,
    plan_name: "plus",
    expires_at: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [notice, setNotice] = useState(null); // { type, message, details? }

  const [applyState, setApplyState] = useState(null); // { running, cursor, applied, skipped }
  const [revokeState, setRevokeState] = useState(null); // { running, cursor, revoked, restored }
  const applyAbortRef = useRef(false);
  const revokeAbortRef = useRef(false);

  const [revokeConfirmOpen, setRevokeConfirmOpen] = useState(false);

  useEffect(() => {
    const data = grantQuery.data;
    if (!data) return;
    // API returns either {id,...} or {grant:{...}} depending on caller; normalize.
    const g = data?.grant ? data.grant : data;
    if (!g) return;
    setForm({
      active: Boolean(g.active),
      plan_name: (g.plan_name || "plus").toLowerCase() === "pro" ? "pro" : "plus",
      expires_at: toDatetimeLocal(g.expires_at),
    });
  }, [grantQuery.data]);

  const validate = () => {
    const errors = {};
    if (!form.plan_name) errors.plan_name = "Plan is required.";
    if (form.active) {
      const iso = toIsoFromDatetimeLocal(form.expires_at);
      if (!iso) errors.expires_at = "Valid end datetime is required.";
      else {
        const d = new Date(iso);
        if (d.getTime() <= Date.now()) errors.expires_at = "End datetime must be in the future.";
      }
    }
    return errors;
  };

  const buildPayload = () => ({
    active: Boolean(form.active),
    plan_name: form.plan_name,
    expires_at: toIsoFromDatetimeLocal(form.expires_at),
  });

  const isBusy = upsertMutation.isPending || applyMutation.isPending || revokeMutation.isPending || applyState?.running || revokeState?.running;

  const noticeStyles = {
    success: "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
    warning: "bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300",
    error: "bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
    info: "bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300",
  };

  const currentGrant = useMemo(() => {
    const data = grantQuery.data;
    return data?.grant ? data.grant : data;
  }, [grantQuery.data]);

  const status = useMemo(() => computeGrantStatus(currentGrant), [currentGrant]);
  const statusBadgeClass = statusStyles[status.tone] || statusStyles.neutral;

  const handleSave = async (e) => {
    e.preventDefault();
    setNotice(null);
    const errors = validate();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const payload = buildPayload();
      const res = await upsertMutation.mutateAsync(payload);
      const warning = res?.warning;
      if (warning) setNotice({ type: "warning", message: "Saved, with warnings.", details: String(warning) });
      else setNotice({ type: "success", message: "Global access grant saved." });

      // Product rule: turning off should revoke immediately.
      if (payload.active === false) {
        setNotice({ type: "info", message: "Disabled. Revoking global access grant now…" });
        await runRevokeLoop();
      }
    } catch (err) {
      const status = err?.status;
      setNotice({
        type: "error",
        message: status ? `Save failed (HTTP ${status}): ${err?.message || "Unknown error"}` : `Save failed: ${err?.message || "Unknown error"}`,
        details: err?.data ? JSON.stringify(err.data, null, 2) : null,
      });
    }
  };

  const runApplyLoop = async () => {
    applyAbortRef.current = false;
    setNotice(null);
    setApplyState({ running: true, cursor: null, applied: 0, skipped: 0 });

    let cursor = null;
    let applied = 0;
    let skipped = 0;

    for (let i = 0; i < 100000; i += 1) {
      if (applyAbortRef.current) break;
      try {
        const res = await applyMutation.mutateAsync({ cursor, limit: DEFAULT_LIMIT });
        applied += Number(res?.applied_count || 0);
        skipped += Number(res?.skipped_count || 0);
        cursor = res?.next_cursor || null;
        setApplyState({ running: Boolean(cursor) && !applyAbortRef.current, cursor, applied, skipped });
        if (!cursor) break;
        // light backoff to reduce server pressure
        await sleep(80);
      } catch (err) {
        setNotice({ type: "error", message: err?.message || "Apply failed.", details: err?.data ? JSON.stringify(err.data, null, 2) : null });
        break;
      }
    }

    setApplyState((s) => (s ? { ...s, running: false, cursor: null } : null));
  };

  const runRevokeLoop = async () => {
    revokeAbortRef.current = false;
    setNotice(null);
    setRevokeState({ running: true, cursor: null, revoked: 0, restored: 0 });

    let cursor = null;
    let revoked = 0;
    let restored = 0;

    for (let i = 0; i < 100000; i += 1) {
      if (revokeAbortRef.current) break;
      try {
        const res = await revokeMutation.mutateAsync({ cursor, limit: DEFAULT_LIMIT });
        revoked += Number(res?.revoked_count || 0);
        restored += Number(res?.restored_count || 0);
        cursor = res?.next_cursor || null;
        setRevokeState({ running: Boolean(cursor) && !revokeAbortRef.current, cursor, revoked, restored });
        if (!cursor) break;
        await sleep(80);
      } catch (err) {
        setNotice({ type: "error", message: err?.message || "Revoke failed.", details: err?.data ? JSON.stringify(err.data, null, 2) : null });
        break;
      }
    }

    setRevokeState((s) => (s ? { ...s, running: false, cursor: null } : null));
    // Refresh config after revoke
    try {
      await grantQuery.refetch?.();
    } catch {
      // ignore
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className={`${typography.h2} ${textColors.primary}`}>Global Access Grant</h1>
          <p className={`${typography.body} ${textColors.secondary} mt-1`}>
            Automatically grant Plus/Pro access to eligible users until a fixed end time. This does not use a redeemable code.
          </p>
        </div>

        <Card className="border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20">
          <CardHeader title="Current Status" />
          <CardBody>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className={`${typography.h4} ${textColors.primary}`}>
                  {currentGrant ? String(currentGrant.plan_name || "plus").toUpperCase() : "—"}
                </div>
                <div className={`${typography.bodySmall} ${textColors.secondary}`}>{status.subtitle}</div>
              </div>
              <div className={`${typography.bodySmall} ${textColors.primary}`}>
                <span className={`px-2 py-1 rounded ${statusBadgeClass}`}>{status.badge}</span>
              </div>
            </div>

            <Divider className="my-4" />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                <span className={`${textColors.primary}`}>Enabled:</span> {currentGrant ? (currentGrant.active ? "Yes" : "No") : "—"}
              </div>
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                <span className={`${textColors.primary}`}>Ends at:</span> {currentGrant?.expires_at ? formatLocalDateTime(currentGrant.expires_at) : "—"}
              </div>
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                <span className={`${textColors.primary}`}>Server id:</span> {currentGrant?.id != null ? String(currentGrant.id) : "—"}
              </div>
            </div>
          </CardBody>
        </Card>

        <Card variant="glass">
          <CardHeader title="Configuration" subtitle="Save config, then apply in batches." />
          <CardBody>
            <form onSubmit={handleSave} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label="Plan" htmlFor="global-access-grant-plan" required>
                  <Select
                    id="global-access-grant-plan"
                    value={form.plan_name}
                    onChange={(e) => setForm((p) => ({ ...p, plan_name: e.target.value }))}
                    options={[
                      { value: "plus", label: "Plus" },
                      { value: "pro", label: "Pro" },
                    ]}
                    placeholder={null}
                    error={formErrors.plan_name}
                  />
                </FormField>

                <FormField label="Ends at" htmlFor="global-access-grant-expires" required={form.active} helperText="When enabled, must be in the future.">
                  <Input
                    id="global-access-grant-expires"
                    type="datetime-local"
                    value={form.expires_at}
                    onChange={(e) => setForm((p) => ({ ...p, expires_at: e.target.value }))}
                    error={formErrors.expires_at}
                  />
                </FormField>

                <div className="flex items-end">
                  <label className={`inline-flex items-center gap-2 ${typography.bodySmall} ${textColors.primary}`}>
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                      className="accent-blue-600 dark:accent-blue-500"
                    />
                    Enabled
                  </label>
                </div>
              </div>

              {notice && (
                <div className={`rounded-2xl px-4 py-3 ${noticeStyles[notice.type] || noticeStyles.info}`}>
                  <p className={`${typography.bodySmall} whitespace-pre-wrap`}>{notice.message}</p>
                  {notice.details ? (
                    <pre className={`mt-2 ${typography.caption} whitespace-pre-wrap break-words font-mono ${textColors.secondary}`}>
                      {notice.details}
                    </pre>
                  ) : null}
                </div>
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <div className={`${typography.caption} ${textColors.tertiary} min-w-0`}>
                  Tip: Save updates the config; “Apply now” updates users in batches.
                </div>
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
                  <SecondaryButton
                    className="w-full sm:w-auto whitespace-nowrap"
                    type="button"
                    onClick={() => setFormErrors({})}
                    disabled={isBusy}
                  >
                    Clear errors
                  </SecondaryButton>
                  <PrimaryButton
                    className="w-full sm:w-auto whitespace-nowrap"
                    type="submit"
                    loading={upsertMutation.isPending}
                    disabled={isBusy}
                  >
                    Save
                  </PrimaryButton>
                </div>
              </div>
            </form>
          </CardBody>
        </Card>

        <Card variant="glass">
          <CardHeader title="Apply to users (batch)" subtitle="Applies to free users and extends eligible trials up to the configured end time." />
          <CardBody className="space-y-4">
            <div className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-4`}>
              <div className={`${typography.bodySmall} ${textColors.primary} font-medium`}>Batch progress</div>
              <div className={`${typography.caption} ${textColors.secondary} mt-1`}>
                Applied: {applyState?.applied ?? 0} · Skipped: {applyState?.skipped ?? 0}{" "}
                {applyState?.running ? "· Running…" : ""}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <PrimaryButton
                type="button"
                onClick={runApplyLoop}
                loading={applyState?.running || applyMutation.isPending}
                disabled={isBusy || !currentGrant || !currentGrant.active}
              >
                Apply now
              </PrimaryButton>
              <SecondaryButton
                type="button"
                onClick={() => {
                  applyAbortRef.current = true;
                  setApplyState((s) => (s ? { ...s, running: false } : s));
                }}
                disabled={!applyState?.running}
              >
                Stop
              </SecondaryButton>
            </div>
          </CardBody>
        </Card>

        <Card variant="glass">
          <CardHeader title="Turn off (immediate revoke)" subtitle="Stops auto-apply and removes global grant effects immediately." />
          <CardBody className="space-y-4">
            <div className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-4`}>
              <div className={`${typography.bodySmall} ${textColors.primary} font-medium`}>Revoke progress</div>
              <div className={`${typography.caption} ${textColors.secondary} mt-1`}>
                Revoked (ended): {revokeState?.revoked ?? 0} · Restored (kept underlying grant): {revokeState?.restored ?? 0}{" "}
                {revokeState?.running ? "· Running…" : ""}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <PrimaryButton
                type="button"
                onClick={() => setRevokeConfirmOpen(true)}
                disabled={isBusy}
              >
                Revoke now
              </PrimaryButton>
              <SecondaryButton
                type="button"
                onClick={() => {
                  revokeAbortRef.current = true;
                  setRevokeState((s) => (s ? { ...s, running: false } : s));
                }}
                disabled={!revokeState?.running}
              >
                Stop
              </SecondaryButton>
            </div>

            <Divider />

            <p className={`${typography.caption} ${textColors.tertiary}`}>
              Note: Users who had an existing access grant will keep it until its original expiry. Users granted solely via the global grant will revert immediately.
            </p>
          </CardBody>
        </Card>

        <ConfirmDialog
          isOpen={revokeConfirmOpen}
          onClose={() => setRevokeConfirmOpen(false)}
          onConfirm={async () => {
            setRevokeConfirmOpen(false);
            await runRevokeLoop();
          }}
          title="Revoke global access grant now?"
          message="This will disable auto-apply and immediately remove global-granted access from users."
          variant="danger"
          confirmText="Revoke"
          cancelText="Cancel"
          isLoading={revokeState?.running || revokeMutation.isPending}
        />
      </div>
    </div>
  );
};

export default GlobalAccessGrantAdminPage;

